import appSettings from "@/appsettings.json";
import { Box, Button, DefaultMantineColor, Group, List, ListItem, Progress } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ReactNode, useEffect, useState } from "react";
import { User } from "./user-objects";

export class Utils {
	public static getColorFromString(str: string): DefaultMantineColor {
		const colors = ["red", "pink", "grape", "violet", "indigo", "blue", "cyan", "teal", "green", "lime", "yellow", "orange"];
		const colorIndex =
			Array.from(str).reduce((acc, char) => acc + char.charCodeAt(0), 0) %
			colors.length;
		return colors[colorIndex] as DefaultMantineColor;
	}

	public static formatUser(u: User): string {
		if (u.firstName && u.lastName) {
			return `${u.firstName} ${u.lastName}`;
		} else {
			return u.username;
		}
	}

	public static escapeRegExp(str: string) {
		return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
	}

	public static replaceAll(str: string, find: string, replace: string) {
		return str.replace(new RegExp(Utils.escapeRegExp(find), 'g'), replace);
	}

	public static async createFilePicker(
		callback: (file: File) => void
	) {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = ".mp4,.mov,.avi,.mkv,.webm";
		input.onchange = (e) => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				callback(file);
			}
		};
		input.click();
	}

	public static async downloadBlob(
		url: string,
		filename: string,
		successIcon: React.ReactNode,
		failIcon: React.ReactNode
	) {
		const id = Utils.displayNotification(
			"Downloading...",
			"Please wait while your file is being downloaded.",
			"blue",
			"loading"
		);
		try {
			const res = await fetch(url, { cache: "force-cache" });
			if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
			const blob = await res.blob();
			const blobUrl = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = blobUrl;
			a.download = filename;
			a.click();
			URL.revokeObjectURL(blobUrl);
			Utils.updateNotification(
				id,
				"Download Complete",
				`Your file has been downloaded as ${filename}.`,
				"green",
				successIcon
			);
		} catch (err) {
			console.error("Download error:", err);
			Utils.updateNotification(id, "Download Failed", "An error occurred while downloading the file.", "red", failIcon);
		}
	};

	public static openProject(projectId: string): void {
		// Open a new tab with the project URL
		window.open(`${appSettings.AppBaseUrl}?projectId=${projectId}`, "_blank");
	}

	// The datebase returns dates in UTC but does not include the "Z" at the end, so this is a workaround
	public static assumeUtcDate(dateString: string): Date {
		const utcString = dateString.endsWith("Z") ? dateString : `${dateString}Z`;
		const parsedUtc = new Date(utcString);
		return parsedUtc;
	}

	public static generateGUID(): string {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			const r = (Math.random() * 16) | 0,
				v = c === 'x' ? r : (r & 0x3) | 0x8;
			return v.toString(16);
		});
	}

	public static formatBytes(bytes: number): string {
		if (!Number.isFinite(bytes)) return "0 B";
		const units = ["B", "KB", "MB", "GB", "TB"];
		const idx = Math.min(units.length - 1, Math.floor(Math.log10(Math.max(bytes, 1)) / 3));
		const val = bytes / Math.pow(1024, idx);
		return `${val.toFixed(val >= 100 || idx === 0 ? 0 : val >= 10 ? 1 : 2)} ${units[idx]}`;
	}

	/**
	 * Ask user to confirm proceeding with a large upload.
	 * - Returns true to continue, false to abort.
	 */
	public static async confirmLargeUpload(file: File, maxBytes: number): Promise<boolean> {
		if (!file) return false;
		if (file.size <= maxBytes) return true;

		const fileSize = Utils.formatBytes(file.size);
		const maxSize = Utils.formatBytes(maxBytes);

		return new Promise<boolean>((resolve) => {
			let settled = false;

			const onResolve = (val: boolean) => {
				if (!settled) {
					settled = true;
					resolve(val);
				}
			};

			const id = Utils.displayNotification(
				"Large video file detected",
				(
					<Box>
						<Box style={{ marginBottom: 6 }}>
							This file is <b>{fileSize}</b>, which is more than the recommended file size limit of <b>{maxSize}</b>.
						</Box>
						<List size="sm">
							<ListItem><b>You may continue</b>, but processing could take <b>longer than usual.</b></ListItem>
							<ListItem>Consider converting your file to a compressed <b>MP4</b>.</ListItem>
							<List withPadding size="sm"><ListItem><a href="https://cloudconvert.com/mov-to-mp4" target="_blank" rel="noopener noreferrer">Click here for an online video converter.</a></ListItem></List>
						</List>
						<Group justify="flex-end" gap="sm">
							<Button variant="default" onClick={() => { onResolve(false); notifications.hide(id); }}>
								Abort
							</Button>
							<Button color="yellow" onClick={() => { onResolve(true); notifications.hide(id); }}>
								Upload Anyway
							</Button>
						</Group>
					</Box>
				),
				"blue",
				undefined,          // no icon
				true,               // withCloseButton
				false,              // do not auto-close
				undefined,
				() => onResolve(false) // closing the notification counts as abort
			);
		});
	}

	private static notificationStyle = {
		top: "5vh"
	};

	// A simple active notifications tracker
	private static activeNotifications = new Set<string>();

	public static displayNotification(
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		id?: string,
		onClose?: () => void
	): string {
		// Use the NotificationContent component only when autoClose is a number.
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);


		if (id !== undefined) {
			if (Utils.activeNotifications.has(id)) {
				Utils.updateNotification(id, title, message, color, icon, withCloseButton, autoClose);
				return id;
			} else {
				Utils.activeNotifications.add(id);
			}
		}
		if (icon === "loading") {
			return notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		} else {
			return notifications.show({
				id: id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		}
	}

	public static updateNotification(
		id: string,
		title: string,
		message: ReactNode | undefined,
		color: DefaultMantineColor,
		icon: ReactNode | "loading",
		withCloseButton: boolean = true,
		autoClose: number | boolean = 3000,
		onClose?: () => void
	) {
		const content =
			typeof autoClose === 'number' && icon !== "loading" ? (
				<NotificationContent message={message} time={autoClose} />
			) : (
				message
			);

		if (!Utils.activeNotifications.has(id)) {
			Utils.activeNotifications.add(id);
		}

		if (icon === "loading") {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				loading: true,
				withCloseButton: false, // No close icon on loading notifications
				autoClose: false,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		} else {
			notifications.update({
				id,
				position: 'top-center',
				color,
				title,
				message: content,
				icon,
				loading: false,
				withCloseButton,
				autoClose,
				style: Utils.notificationStyle,
				onClose: () => {
					if (id !== undefined) {
						Utils.activeNotifications.delete(id);
					}
					onClose?.(); // Call the onClose callback if provided
				}
			});
		}
	}
}

interface NotificationContentProps {
	message?: ReactNode;
	time: number;
}

const NotificationContent: React.FC<NotificationContentProps> = ({ message, time }) => {
	const [progress, setProgress] = useState(100);

	useEffect(() => {
		const startTime = Date.now();
		const timer = setInterval(() => {
			const elapsed = Date.now() - startTime;
			const percentage = Math.max(0, Math.min(100, 100 - ((elapsed / time) * 100)));
			setProgress(percentage);
		}, 50); // update every 50ms
		return () => clearInterval(timer);
	}, [time]);

	return (
		<>
			{message}
			{/* Render the progress bar only if autoClose is a number */}
			<Progress
				value={progress}
				size="xs"
				mt={8}
				color="yellow"
			/>
		</>
	);
};
